import { ValidationError } from 'yup';

// Standard error interface used throughout the application
export interface ValidationErrorItem {
    path: string;
    message: string;
    code?: string; // Optional error code for more specific handling
}

export interface ValidationResult {
    isValid: boolean;
    errors: ValidationErrorItem[] | null;
}

export class ErrorHandlingService {
    /**
     * Converts Yup validation errors to standard format
     */
    static convertYupError(error: ValidationError): ValidationErrorItem[] {
        return (
            error.inner?.map(err => ({
                path: err.path || '',
                message: err.message,
                code: err.type || 'validation_error',
            })) || [
                {
                    path: '',
                    message: error.message,
                    code: 'validation_error',
                },
            ]
        );
    }

    /**
     * Converts API errors to standard format
     */
    static convertApiError(error: any): ValidationErrorItem[] {
        // Handle various API error formats
        if (error?.response?.data?.PropertiesNotValid) {
            return error.response.data.PropertiesNotValid.split(', ').map((msg: string) => ({ path: '', message: msg, code: 'api_validation_error' }));
        }

        return [
            {
                path: '',
                message: error.message || 'Unknown API error',
                code: 'api_error',
            },
        ];
    }

    /**
     * Converts standard errors to user-friendly messages for display
     */
    static getErrorDisplayMessage(errors: ValidationErrorItem[]): string {
        return errors.map(err => err.message).join('\n');
    }

    /**
     * Helper to create a failed validation result
     */
    static createValidationError(message: string, path = ''): ValidationResult {
        return {
            isValid: false,
            errors: [{ path, message, code: 'validation_error' }],
        };
    }

    /**
     * Helper to create a successful validation result
     */
    static createValidationSuccess(): ValidationResult {
        return { isValid: true, errors: null };
    }
}
